import { Component, computed, DestroyRef, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucideListFilter, lucideLockKeyhole, lucideLockKeyholeOpen } from '@ng-icons/lucide';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmSelectImports } from '@spartan-ng/helm/select';

import { Site } from '../../../administration/gestion-sites/model/site.model';
import { UserStore } from '../../../auth/store/user.store';
import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { CartographieStore } from '../../../cartographie/store/cartographie.store';
import { DialogSelectCfaComponent } from '../../../catalogue-national/components/dialog-select-cfa/dialog-select-cfa.component';
import { BadgeDirective } from '../../../shared/directives/badge.directive';
import { TrierParPipe } from '../../../shared/pipes/trier-par.pipe';
import { FiltresOffreRegionale } from '../../offre-regionale.component';
import { ChipComponent } from '@shared/components/chip/chip.component';
import { DialogVerrouillageOffreRegionaleComponent } from '../dialog-verrouillage-offre-regionale/dialog-verrouillage-offre-regionale.component';

@Component({
  selector: 'app-header',
  imports: [
    HlmButtonImports,
    HlmIconImports,
    BrnSelectImports,
    HlmSelectImports,
    TrierParPipe,
    BadgeDirective,
    ChipComponent,
  ],
  templateUrl: './header.component.html',
  providers: [provideIcons({ lucideLockKeyhole, lucideListFilter, lucideLockKeyholeOpen })],
})
export class HeaderComponent implements OnInit {
  readonly #cartographieStore = inject(CartographieStore);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #userStore = inject(UserStore);

  readonly cartographie = input.required<Cartographie>();
  readonly filtres = input.required<FiltresOffreRegionale>();

  readonly onFilter = output();

  isAdmin = computed(() => this.#userStore.isAdmin());
  readonly cartographies = computed(() => this.#cartographieStore.actives());
  readonly nbFiltres = computed(() => Object.values(this.filtres()).filter((x) => !!x).length);

  ngOnInit(): void {
    this.#cartographieStore.charger();
  }

  setCartographieCourante(cartographie: Cartographie) {
    this.#cartographieStore.select(cartographie);
    this.#router.navigate(['cartographies', cartographie.slug, 'offre-régionale']);
  }

  selectionnerSite() {
    const dialogRef = this.#dialog.open(DialogSelectCfaComponent, {
      context: {
        cartographie: this.cartographie(),
      },
      contentClass: 'sm:!max-w-[500px]',
    });
    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((site?: Site) => {
      if (!site) return;

      this.#router.navigate([site.id], { relativeTo: this.#route });
    });
  }

  ouvreCatalogueNational() {
    this.#router.navigate(['cartographies', this.cartographie().slug, 'catalogue-national']);
  }

  toggleVerouillageOffreRegionale() {
    const dialogRef = this.#dialog.open(DialogVerrouillageOffreRegionaleComponent, {
      context: {
        cartographie: this.cartographie(),
      },
      contentClass: 'sm:!max-w-[500px]',
    });
    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.#cartographieStore.modifier({
        id: this.cartographie().id,
        data: {
          estVerrouilleeOffreRegionale: !this.cartographie().estVerrouilleeOffreRegionale,
        },
      });
    });
  }
}
